import React from "react";

/**
 * Round 2 — Modular + Editorial focused concepts.
 * User picked Stack (modular) and Marque (editorial) as directions they liked.
 * These 8 explore deeper into those two lanes.
 */

const CYAN = "#22D3EE";
const WHITE = "#F8FAFC";
const SLATE = "#475569";
const SLATE_DIM = "#334155";

/* ============================================================
 *                    MODULAR CONCEPTS
 * ============================================================ */

/* 01 — Lattice
   Five vertical bars of varying heights, like a rhythmic
   equalizer. Center bar cyan. Reads as structured + dynamic. */
export const LogoLattice = ({ size = 56 }) => {
  const heights = [20, 36, 52, 36, 20];
  const bars = heights.map((h, i) => {
    const x = 10 + i * 9;
    const y = 32 - h / 2;
    const fill =
      i === 2 ? CYAN : i === 1 || i === 3 ? WHITE : SLATE;
    return <rect key={i} x={x} y={y} width="6" height={h} rx="1.5" fill={fill} />;
  });
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
      {bars}
    </svg>
  );
};

/* 02 — Offset
   Four horizontal bars stacked like bricks, each offset.
   Second-from-top is cyan. Gives the mark a "built" feel. */
export const LogoOffset = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="14" width="30" height="7" rx="1.5" fill={WHITE} />
    <rect x="20" y="23" width="30" height="7" rx="1.5" fill={CYAN} />
    <rect x="12" y="32" width="30" height="7" rx="1.5" fill={WHITE} />
    <rect x="18" y="41" width="30" height="7" rx="1.5" fill={SLATE} />
  </svg>
);

/* 03 — Matrix
   A sparse 5x5 grid of squares. Filled cells form an abstract
   left-facing "C" with two cyan accent cells. Technical + precise. */
export const LogoMatrix = ({ size = 56 }) => {
  // 1 = white, 2 = cyan
  const grid = [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 0],
    [2, 0, 0, 0, 0],
    [1, 0, 0, 0, 0],
    [0, 1, 1, 2, 0],
  ];
  const cell = 8;
  const gap = 2;
  const origin = 9;
  const cells = [];
  grid.forEach((row, ri) =>
    row.forEach((v, ci) => {
      if (!v) return;
      cells.push(
        <rect
          key={`${ri}-${ci}`}
          x={origin + ci * (cell + gap)}
          y={origin + ri * (cell + gap)}
          width={cell}
          height={cell}
          rx="1.2"
          fill={v === 2 ? CYAN : WHITE}
        />
      );
    })
  );
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
      {cells}
    </svg>
  );
};

/* 04 — Kit (isometric stack)
   Three modular blocks stacked in isometric perspective.
   Top block cyan — like the "custom piece" being placed. */
export const LogoKit = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* bottom block */}
    <g>
      <path d="M32 52 L52 42 L52 36 L32 46 L12 36 L12 42 Z" fill={SLATE} />
      <path d="M32 46 L52 36 L32 26 L12 36 Z" fill={WHITE} opacity="0.85" />
    </g>
    {/* middle block */}
    <g transform="translate(0 -10)">
      <path d="M32 42 L46 35 L46 31 L32 38 L18 31 L18 35 Z" fill={SLATE_DIM} />
      <path d="M32 38 L46 31 L32 24 L18 31 Z" fill={WHITE} />
    </g>
    {/* top cyan block */}
    <g transform="translate(0 -20)">
      <path d="M32 34 L42 29 L42 26 L32 31 L22 26 L22 29 Z" fill={CYAN} opacity="0.7" />
      <path d="M32 31 L42 26 L32 21 L22 26 Z" fill={CYAN} />
    </g>
  </svg>
);

/* ============================================================
 *                    EDITORIAL CONCEPTS
 * ============================================================ */

/* 05 — Stencil
   A single ultra-heavy lowercase "c" with a cyan period.
   Magazine-cover minimalism, premium and confident. */
export const LogoStencil = ({ size = 56 }) => (
  <svg viewBox="0 0 96 64" width={size * 1.5} height={size} xmlns="http://www.w3.org/2000/svg">
    <text
      x="4"
      y="52"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 900,
        fontSize: "64px",
        letterSpacing: "-4px",
      }}
    >
      c
    </text>
    <rect x="62" y="46" width="8" height="8" rx="1.5" fill={CYAN} />
  </svg>
);

/* 06 — Slash
   "c/c/g" lowercase heavy wordmark with cyan forward slashes.
   Fashion-editorial / archival catalog feel. */
export const LogoSlash = ({ size = 56 }) => (
  <svg viewBox="0 0 128 64" width={size * 2} height={size} xmlns="http://www.w3.org/2000/svg">
    <text
      x="4"
      y="48"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 800,
        fontSize: "44px",
        letterSpacing: "-2px",
      }}
    >
      c
    </text>
    <path d="M38 12 L29 54" stroke={CYAN} strokeWidth="4" strokeLinecap="round" />
    <text
      x="44"
      y="48"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 800,
        fontSize: "44px",
        letterSpacing: "-2px",
      }}
    >
      c
    </text>
    <path d="M82 12 L73 54" stroke={CYAN} strokeWidth="4" strokeLinecap="round" />
    <text
      x="88"
      y="48"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 800,
        fontSize: "44px",
        letterSpacing: "-2px",
      }}
    >
      g
    </text>
  </svg>
);

/* 07 — Ligature
   Two lowercase c's merged into a custom ligature with
   a cyan terminal dot. A true custom letterform. */
export const LogoLigature = ({ size = 56 }) => (
  <svg viewBox="0 0 96 64" width={size * 1.5} height={size} xmlns="http://www.w3.org/2000/svg">
    {/* outer c */}
    <path
      d="M46 14
         A18 18 0 0 0 10 32
         A18 18 0 0 0 46 50"
      stroke={WHITE}
      strokeWidth="8"
      strokeLinecap="round"
      fill="none"
    />
    {/* inner overlapping c (shifted right) */}
    <path
      d="M78 16
         A16 16 0 0 0 46 32
         A16 16 0 0 0 78 48"
      stroke={WHITE}
      strokeWidth="8"
      strokeLinecap="round"
      fill="none"
      opacity="0.55"
    />
    {/* cyan terminal */}
    <circle cx="80" cy="48" r="4.5" fill={CYAN} />
  </svg>
);

/* 08 — Column
   "CCG" stacked vertically in heavy uppercase with a cyan
   rule underneath. Modernist poster typography as a mark. */
export const LogoColumn = ({ size = 56 }) => (
  <svg viewBox="0 0 64 96" width={size * 0.666} height={size} xmlns="http://www.w3.org/2000/svg">
    <text
      x="32"
      y="24"
      textAnchor="middle"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 900,
        fontSize: "26px",
        letterSpacing: "-1px",
      }}
    >
      C
    </text>
    <text
      x="32"
      y="50"
      textAnchor="middle"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 900,
        fontSize: "26px",
        letterSpacing: "-1px",
      }}
    >
      C
    </text>
    <text
      x="32"
      y="76"
      textAnchor="middle"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 900,
        fontSize: "26px",
        letterSpacing: "-1px",
      }}
    >
      G
    </text>
    <rect x="18" y="84" width="28" height="3" rx="1" fill={CYAN} />
  </svg>
);

/* Registry */
export const LOGO_OPTIONS_V2 = [
  {
    id: "lattice",
    name: "Lattice",
    tagline: "Modular · rhythmic",
    group: "Modular",
    description:
      "Five vertical bars at varied heights, rising and falling like an equalizer. The cyan center bar anchors the rhythm. Dynamic, confident, and feels like a system in motion.",
    Component: LogoLattice,
  },
  {
    id: "offset",
    name: "Offset",
    tagline: "Modular · built",
    group: "Modular",
    description:
      "Four horizontal bars stacked like masonry, each shifted horizontally from the last. Cyan second-from-top. Reads as construction / engineered assembly.",
    Component: LogoOffset,
  },
  {
    id: "matrix",
    name: "Matrix",
    tagline: "Modular · technical",
    group: "Modular",
    description:
      "A sparse 5×5 grid where filled cells trace an abstract C. Two cyan accent cells punctuate the pattern. Feels like a system diagram / data matrix.",
    Component: LogoMatrix,
  },
  {
    id: "kit",
    name: "Kit",
    tagline: "Modular · dimensional",
    group: "Modular",
    description:
      "Three isometric blocks stacked in perspective, with the top block in cyan — the custom module being placed. Communicates 'engineered, assembled, built to fit.'",
    Component: LogoKit,
  },
  {
    id: "stencil",
    name: "Stencil",
    tagline: "Editorial · monogram",
    group: "Editorial",
    description:
      "A single ultra-heavy lowercase 'c' with a cyan period beside it. Ultra-minimal, magazine-cover confident. Pairs beautifully with a spelled-out 'Cleveland Creative Group' wordmark.",
    Component: LogoStencil,
  },
  {
    id: "slash",
    name: "Slash",
    tagline: "Editorial · typographic",
    group: "Editorial",
    description:
      "'c/c/g' with cyan forward slashes cutting between letters. Fashion-editorial, archival, confident. High-impact at small sizes.",
    Component: LogoSlash,
  },
  {
    id: "ligature",
    name: "Ligature",
    tagline: "Editorial · custom letterform",
    group: "Editorial",
    description:
      "Two lowercase c's merged into a custom ligature with a cyan terminal dot. A proper custom glyph — one-of-a-kind and ownable as a brand signature.",
    Component: LogoLigature,
  },
  {
    id: "column",
    name: "Column",
    tagline: "Editorial · poster",
    group: "Editorial",
    description:
      "C · C · G stacked vertically in heavy uppercase with a single cyan rule underneath. Modernist Swiss poster typography scaled down to a mark.",
    Component: LogoColumn,
  },
];
